import User from "./Crud/User";

/**
 * Handles all of the security checks in the application.
 */
export default class Security {
    static memo = {};
    static context;

    /**
     * @param {object} context The context.
     */
    static setContext(context) {
        Security.context = context;
    }

    /**
     * Clears memoized permission results after permission inputs change.
     */
    static clearMemo() {
        Security.memo = {};
    }

    /**
     * @param {string} permission The permission to check
     * @returns {boolean} Whether or not the current user has that permission.
     */
    static hasPermission(permission) {
        // Assume no permission if this is run with no context.
        if (!Security.context || !Security.context().loaded) {
            return false;
        }

        const user = Security.context().currentUser;
        const key = `${permission}.${user.id}`;

        if (this.memo.hasOwnProperty(key) === false) {
            if (permission.startsWith("dashboard.access.")) {
                const dashboardId = parseInt(permission.slice("dashboard.access.".length), 10);
                this.memo[key] = this.hasDashboardAccess(user, dashboardId);
                return this.memo[key];
            }
            switch (permission) {
                case "queue.viewAll":
                    /**
                     * If you are an admin, an exec, a director, or are not on a
                     * team and are note a manager, then you can view all
                     * queues. Marketing also gets to view everything.
                     */
                    this.memo[key] = (
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT", "MARKETING", "MARKETING_MANAGER"]) ||
                        this.isExecutive(user) ||
                        this.isDirector(user)
                    );
                    break;
                case "queue.useRegionPrefilter":
                    /**
                     * If you are on a team that uses the region prefilter or
                     * you can view all regions.
                     */
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "region") ||
                        this.hasPermission("user.viewAllRegions")
                    );
                    break;
                case "queue.useLocationPrefilter":
                    /**
                     * If you are on a team that uses the location prefilter or
                     * you can view all locations.
                     */
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "location") ||
                        this.hasPermission("user.viewAllLocations")
                    );
                    break;
                case "queue.useUserPrefilter":
                    /**
                     * If you are on a team that uses the user prefilter or
                     * you can view all users.
                     */
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "user") ||
                        this.hasPermission("user.viewAllUsers")
                    );
                    break;
                case "queue.useMarketingCampaignPrefilter":
                    /**
                     * If you are on a team that uses the marketing campaign
                     * prefilter or are an executive or admin.
                     */
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "marketingCampaign") ||
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT"])
                    );
                    break;
                case "queue.usePrimaryProductTypePrefilter":
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "primaryProductType") ||
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT"])
                    );
                    break;
                case "queue.usePrimaryInsuranceTypePrefilter":
                    this.memo[key] = (
                        this.isOnADepartmentThatUsesPrefilter(user, "primaryInsuranceType") ||
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT"])
                    );
                    break;
                case "user.viewAllRegions":
                case "user.viewAllLocations":
                    this.memo[key] = this.hasDatabasePermission(user, 8); // Location - All Locations
                    break;
                case "user.viewAllUsers":
                    /**
                     * Whether or not the user can view all users. Typically
                     * used for filtering a dropdown or search.
                     */
                    this.memo[key] = (
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT", "MARKETING", "MARKETING_MANAGER", "BUSINESS_SUPPORT"]) ||
                        this.isExecutive(user)
                    );
                    break;
                case "user.viewAllUsersForMyLocations":
                    /**
                     * Whether or not the user can view all users for their
                     * assigned locations. Typically used for filtering a
                     * dropdown or search.
                     */
                    this.memo[key] = (
                        this.hasRole(
                            user,
                            [
                                "ADMIN",
                                "EXECUTIVE_MANAGEMENT",
                                "OFFICE_MANAGER",
                                "AREA_MANAGER",
                                "REGIONAL_OPERATIONS_MANAGER",
                                "SALES_MANAGER",
                            ]
                        ) ||
                        this.isExecutive(user)
                    );
                    break;
                case "module.salesScorecard":
                    this.memo[key] = this.hasDatabasePermission(user, 3); // Sales/Scorecard - Access
                    break;
                case "module.myAccounts":
                    this.memo[key] = this.hasDatabasePermission(user, 12); // Sales/Accounts - Access
                    break;
                case "module.myPatients":
                    this.memo[key] = this.hasDatabasePermission(user, 13); // Sales/Patients - Access
                    break;
                case "module.metric":
                    this.memo[key] = this.hasDatabasePermission(user, 14); // Reports/Metric - Access
                    break;
                case "module.ARDashboard":
                    this.memo[key] = this.hasDatabasePermission(user, 31); // Reports/AR Dashboard - Access
                    break;
                case "module.userProductionDetail.invoiceProduction":
                    this.memo[key] = this.hasDatabasePermission(user, 32); // Reports/Invoice Production - Access
                    break;
                case "module.orderTimelineAudit":
                    this.memo[key] = this.hasDatabasePermission(user, 33); // Reports/Order Timeline Audit - Access
                    break;
                case "module.reportHome":
                    this.memo[key] = this.hasDatabasePermission(user, 35); // Reports/Home - Access
                    break;
                case "module.salesPipeline":
                    this.memo[key] = this.hasDatabasePermission(user, 36); // Reports/Sales Pipeline - Access
                    break;
                case "module.monthAtAGlance":
                    this.memo[key] = this.hasDatabasePermission(user, 34); // Reports/Month at a Glance - Access
                    break;
                case "module.salesPcrLinks":
                    this.memo[key] = this.hasDatabasePermission(user, 10); // PCR Links - Manage
                    break;
                case "module.presidentsClub":
                    this.memo[key] = this.hasDatabasePermission(user, 37); // Sales/Presidents Club - Access
                    break;
                case "module.salesBonus":
                    /**
                     * Whether or not the dropdown option in the app bar
                     * renders Sales Bonus
                     */
                    this.memo[key] = (
                        this.hasRole(
                            user,
                            [
                                "ADMIN",
                                "FINANCE",
                            ]
                        )
                    );
                    break;
                case "module.purchasingQueues":
                    this.memo[key] = this.hasDatabasePermission(user, 22); // Purchasing/Purchase Queues - Access
                    break;
                case "module.vendors":
                    this.memo[key] = this.hasDatabasePermission(user, 39); // Purchasing/Vendors - Access
                    break;
                case "module.inventoryRequest":
                    this.memo[key] = (
                        this.hasDatabasePermission(user, 21) // Purchasing/Inventory Request - Access
                    );
                    break;
                case "module.poDeliveryUpdate":
                    this.memo[key] = this.hasDatabasePermission(user, 52); // PO Delivery Update
                    break;
                case "module.shippingDetailsReport":
                    this.memo[key] = this.hasDatabasePermission(user, 51); // Shipping Details
                    break;
                case "module.manageServiceParts":
                    this.memo[key] = this.hasDatabasePermission(user, 4); // Service Part Catalog - Manage
                    break;
                case "module.productCategories":
                    this.memo[key] = this.hasDatabasePermission(user, 40); // Purchasing/Products - Access
                    break;
                case "module.servicePartCategories":
                    this.memo[key] = this.hasDatabasePermission(user, 41); // Purchasing / Service Part Categories - Access
                    break;
                case "module.marketingCampaigns":
                    this.memo[key] = this.hasDatabasePermission(user, 43); // Marketing/Campaigns - Access
                    break;
                case "module.marketingCampaignCategories":
                    this.memo[key] = this.hasDatabasePermission(user, 44); // Marketing/Campaign Categories - Access
                    break;
                case "module.marketingAccounts":
                    this.memo[key] = this.hasDatabasePermission(user, 42); // Marketing / Accounts - Access
                    break;
                case "module.adminUsers":
                case "module.adminTeams":
                    this.memo[key] = (
                        this.hasRole(
                            user,
                            [
                                "ADMIN",
                                "USER_ADMIN",
                            ]
                        )
                    );
                    break;
                case "module.adminRoles":
                case "module.marketingPreferredContactMethods":
                case "module.adminDepartments":
                case "module.adminPayorSources":
                case "module.adminInsuranceGroups":
                case "module.adminAccountTypes":
                case "module.adminContactTypes":
                case "module.adminCreditCardTypes":
                case "module.adminExpenseTypes":
                case "module.adminStatusReasons":
                case "module.adminStatusReasonDetails":
                case "module.adminLocations":
                case "module.adminRegions":
                case "module.adminManualDocumentBuilder":
                case "module.adminMassRoute":
                case "module.adminDeliveryFormAcknowledgements":
                case "module.adminOrderActivityTypes":
                case "module.adminQueues":
                case "module.adminQueueTypes":
                case "module.adminQueueRoutes":
                case "module.adminDashboards":
                case "module.adminWidgets":
                case "module.adminCommissionTiers":
                case "module.adminPlaceOfServices":
                case "module.adminBrightreeInvoiceStatuses":
                case "module.adminInvoiceFollowupStatuses":
                case "module.adminBrightreeStopHoldTypes":
                case "module.adminBrightreeStopHoldReasons":
                case "module.adminBaseUnitTypes":
                case "module.adminBaseUnits":
                case "module.adminBrightreeProducts":
                case "module.adminIssueCategories":
                case "module.adminIssueReasons":
                case "module.adminProductMatrixEntries":
                case "module.adminDistributionTypes":
                case "module.adminDistributionReasons":
                case "module.adminTimeZones":
                case "module.adminHcpcsCodes":
                case "module.adminDiagnosisCodes":
                case "module.adminUserProductionTypes":
                case "module.adminUserProductionDetailTypes":
                case "module.adminServiceQuotePartStatuses":
                case "module.adminServiceQuotePartSources":
                case "module.adminServiceQuotePartReasons":
                case "module.adminPermissions":
                case "module.adminTerms":
                case "module.adminModifierCodes":
                case "module.adminFeeSchedule":
                case "module.adminDistributionReasonTypes":
                case "module.adminNotificationStatuses":
                case "module.adminNotificationTypes":
                case "module.adminNotificationChannels":
                case "module.adminNoteTypes":
                case "module.adminGenderIdentities":
                case "module.adminPreferredPronouns":
                case "module.adminActivityFileTypes":
                case "module.adminEvaluationTypes":
                case "module.adminBusinessSupportRequestResolutions":
                case "module.adminProviders":
                case "productType.manage":
                case "productSubType.manage":
                    this.memo[key] = this.hasDatabasePermission(user, 45); // System Configuration
                    break;
                case "provider.edit":
                    this.memo[key] = this.hasDatabasePermission(user, 9); // Provider - Edit Contact
                    break;
                case "module.adminInsurance":
                    this.memo[key] = (
                        this.hasPermission("insuranceSubType.manage") ||
                        this.hasPermission("insuranceSubType.delete")
                    );
                    break;
                case "module.operationsPatientCollections":
                    this.memo[key] = this.hasDatabasePermission(user, 49); // Operations/Patient Collections - Access
                    break;
                case "order.upgradeToAnything":
                    this.memo[key] = this.hasDatabasePermission(user, 46); // Order - Upgrade to Anything
                    break;
                case "order.removeMarketingCampaign":
                    this.memo[key] = this.hasDatabasePermission(user, 15); // Order - Remove Marketing Campaign
                    break;
                case "order.manualRoute":
                    this.memo[key] = this.hasDatabasePermission(user, 1); // Order - Manual Route
                    break;
                case "order.cancel":
                    this.memo[key] = this.hasDatabasePermission(user, 16); // Order - Cancel
                    break;
                case "order.syncToBrightree":
                    this.memo[key] = this.hasDatabasePermission(user, 18); // Order - Sync to Brightree
                    break;
                case "order.changeParentOrder":
                    this.memo[key] = this.hasDatabasePermission(user, 47); // Order - Change Parent Order
                    break;
                case "order.uploadAllDocumentTypes":
                    this.memo[key] = this.hasDatabasePermission(user, 48); // Order - Upload All Document Types
                    break;
                case "order.approve":
                    this.memo[key] = this.hasDatabasePermission(user, 19); // Order - Approve
                    break;
                case "order.allowEligibilityRestrictionOverride":
                    this.memo[key] = this.hasDatabasePermission(user, 20); // Order - Allow eligibility restriction override
                    break;
                case "order.deliverByDate":
                    this.memo[key] = this.hasDatabasePermission(user, 23); // Order - Update Deliver By Date
                    break;
                case "order.updateScheduledDeliveryDate":
                    this.memo[key] = this.hasDatabasePermission(user, 24); // Order - Update Scheduled Delivery Date
                    break;
                case "patient.mergePatients":
                    this.memo[key] = this.hasDatabasePermission(user, 25); // Patient - Merge
                    break;
                case "patient.insuranceDiscovery":
                    this.memo[key] = this.hasDatabasePermission(user, 50); // Patient - Insurance Discovery
                    break;
                case "patientIssue.createClosePatientCollection":
                    /**
                     * Whether or not the user can create and close patient
                     * issues with the Patient Collections category. This was
                     * specifically asked for and is a Rehab Medical only
                     * permission.
                     */
                    this.memo[key] = (
                        this.hasRole(user, [
                            "ADMIN",
                            "EXECUTIVE_MANAGEMENT"
                        ]) ||
                        this.hasDepartment(user, Security.context().departmentsIndexed[8]) // Revenue cycle
                    );
                    break;
                case "insuranceSubType.manage":
                    this.memo[key] = this.hasDatabasePermission(user, 2); // Insurance Subtype - Manage
                    break;
                case "insuranceSubType.delete":
                    this.memo[key] = this.hasDatabasePermission(user, 26); // Insurance Subtype - Delete
                    break;
                case "patient.updateExternalIds":
                    this.memo[key] = this.hasDatabasePermission(user, 27); // Patient - Update External IDs
                    break;
                case "orderActivity.updateExternalIds":
                    this.memo[key] = this.hasDatabasePermission(user, 17); // Order - Update External IDs
                    break;
                case "order.deleteAnyFile":
                    this.memo[key] = this.hasDatabasePermission(user, 38); // Order - Delete any file
                    break;
                case "file.download":
                    // Kenny approved this for now
                    return true;
                    break;
                case "module.inServiceSummary":
                    this.memo[key] = this.hasDatabasePermission(user, 28); // Sales/In-Service Summary - Access
                    break;
                case "module.manageAtpAssessment":
                    this.memo[key] = (
                        this.hasRole(user, ["ADMIN"]) ||
                        this.isAtp(user)
                    );
                    break;
                case "chart.allChartViews":
                    this.memo[key] = this.hasDatabasePermission(user, 6); // Chart - All Views
                    break;
                case "chart.allDashboards":
                    this.memo[key] = this.hasDatabasePermission(user, 7); // Dashboard - All Dashboards
                    break;
                case "myAccounts.viewAllForOrganization":
                    this.memo[key] = (
                        this.hasRole(user, ["ADMIN", "EXECUTIVE_MANAGEMENT"])
                    );
                    break;
                case "myAccounts.viewAllForLocation":
                    this.memo[key] = (
                        this.hasRole(user, ["ADMIN", "SALES_MANAGER", "EXECUTIVE_MANAGEMENT"])
                    );
                    break;
                case "inServiceSummary.viewAll":
                    this.memo[key] = this.hasDatabasePermission(user, 29); // Sales/In-Service Summary - View All Locations
                    break;
                case "chart.resetParts":
                    this.memo[key] = this.hasDatabasePermission(user, 5); // Order - Reset Part Return
                    break;
                default:
                    this.memo[key] = false;
                    break;
            }
        }

        return this.memo[key];
    }

    /**
     * @param {object} user The user to check.
     * @param {array|string} roles The role(s) to check.
     * @returns {boolean} Whether or not the user has a specific role or roles.
     *
     * Role reference:
     *
     * ADMIN
     * AREA_MANAGER
     * REGIONAL_OPERATIONS_MANAGER
     * BILLING
     * CARE_COORDINATOR
     * COLLECTIONS
     * CUSTOMER_EXPERIENCE
     * EXECUTIVE_MANAGEMENT
     * FINANCE
     * FOLLOWUP
     * MARKETING
     * MARKETING_MANAGER
     * OFFICE_MANAGER
     * OFFICE_SUPPORT
     * PATIENT_CARE_REP
     * PURCHASING
     * SALES
     * BUSINESS_SUPPORT
     * BUSINESS_SUPPORT_MANAGER
     * SALES_MANAGER
     * SERVICE_TEAM
     * TECHNICIAN
     * THIRD_PARTY_USER
     * VERIFICATION
     * WAREHOUSE_TECHNICIAN
     */
    static hasRole(user, roles) {
        if (typeof roles === "string") {
            roles = [roles];
        }
        return roles.includes(user.role);
    }

    /**
     * @param {object} user The user to check.
     * @returns {boolean} Whether or not the user is an executive, based on
     * their attachment as an executive on departments.
     */
    static isExecutive(user) {
        return Object.values(Security.context().departmentsIndexed).some((department) => {
            return department.executiveId === user.id;
        });
    }
    /**
     * @param {object} user The user to check.
     * @returns {boolean} Whether or not the user is a atp, based on the database
     * field isAtp.
     */
    static isAtp(user) {
        return user.isAtp;
    }
    /**
     * @param {object} user The user to check.
     * @returns {boolean} Whether or not the user is a director, based on their
     * attachment as a director on departments. Also checks regions.
     */
    static isDirector(user) {
        return (
            Object.values(Security.context().departmentsIndexed).some((department) => {
                return department.directorId === user.id;
            }) ||
            Object.values(Security.context().regionsIndexed).some((region) => {
                return (
                    region.directorUserprofileId === user.id
                );
            })
        );
    }

    /**
     * @param {object} user The user to check.
     * @returns {boolean} Whether or not the user is a manager, based on their
     * attachment as a manager on teams.
     */
    static isManager(user) {
        return Object.values(Security.context().teamsIndexed).some((team) => {
            return team.managerId === user.id;
        });
    }

    /**
     * @param {object} user The user to check.
     * @param {object} department The department to check.
     * @returns {boolean} Whether or not the user is on the specified department.
     */
    static hasDepartment(user, department) {
        return (
            department?.directorId === user.id ||
            department?.executiveId === user.id ||
            (
                Object.values(Security.context().teamsIndexed).some((team) => {
                    return (
                        team.departmentId === department.id &&
                        User.hasTeam(team, user, Security.context().teamUsersIndexed)
                    );
                })
            )
        );
    }

    /**
     * @param {object} user The user to check.
     * @param {number} dashboardId The dashboard_id to check access for.
     * @returns {boolean} Whether or not the user has access to the given
     * dashboard: either they hold permission id 7 ("Dashboard - All
     * Dashboards"), or they're on a team that has a team_dashboard row
     * pointing at this dashboard.
     */
    static hasDashboardAccess(user, dashboardId) {
        if (this.hasPermission("chart.allDashboards")) {
            return true;
        }

        return Object.values(Security.context().teamDashboardsIndexed).some((teamDashboard) => {
            if (teamDashboard.dashboardId !== dashboardId) {
                return false;
            }
            const team = Security.context().teamsIndexed[teamDashboard.teamId];
            return User.hasTeam(team, user, Security.context().teamUsersIndexed);
        });
    }

    /**
     * @param {object} user The user to check.
     * @returns {boolean} Whether or not the user is on a team, based on whether
     * or not at least one row exists in team_user with their user.id.
     */
    static isOnATeam(user) {
        return Object.values(Security.context().teamUsersIndexed).some((teamUser) => {
            return teamUser.userId === user.id;
        });
    }

    /**
     * @param {object} user The user to check.
     * @param {string} type The type of prefilter
     * region|location|user|marketingCampaign|primaryProductType|primaryInsuranceType
     * @returns {boolean} Whether or not the user is on a team that uses
     * prefilters, based on whether or not at least one row exists in team_user
     * with their user.id and that team is on a department with usePrefilters
     * enabled.
     */
    static isOnADepartmentThatUsesPrefilter(user, type) {
        const departments = Object.values(Security.context().departmentsIndexed)
            .filter((department) =>
                department.jsonPrefilters?.enabled === true &&
                department.jsonPrefilters?.[type] === true
            );

        for (let i = 0; i < departments.length; i++) {
            if (this.hasDepartment(user, departments[i])) {
                return true;
            }
        }
    }

    /**
     * Checks if the given user has a specific permission, either directly or
     * via any of their assigned roles. This uses the context's
     * userprofilePermissionsIndexed, userprofileRolesIndexed, and
     * rolePermissionsIndexed objects to determine access. Explicit denies take
     * precedence over grants.
     *
     * @param {object} user The user object to check.
     * @param {number} permissionId The permission_id to check for.
     * @returns {boolean} True if the user has the permission, false otherwise.
     */
    static hasDatabasePermission(user, permissionId) {
        // Check explicit user permission grants/denies first
        const userprofilePermissions = Object.values(Security.context().userprofilePermissionsIndexed).filter(
            userprofilePermission =>
                userprofilePermission.userProfileId === user.id &&
                userprofilePermission.permissionId === permissionId
        );
        // If any explicit deny, return false
        if (userprofilePermissions.some(userprofilePermission => userprofilePermission.deny)) {
            return false;
        }
        // If any explicit grant, return true
        if (userprofilePermissions.some(userprofilePermission => !userprofilePermission.deny)) {
            return true;
        }

        // Otherwise, check via roles
        const userprofileRoles = Object.values(Security.context().userprofileRolesIndexed).filter(
            userprofileRole =>
                userprofileRole.userProfileId === user.id
        );
        for (const userprofileRole of userprofileRoles) {
            const rolePermissions = Object.values(Security.context().rolePermissionsIndexed).filter(
                rolePermission =>
                    rolePermission.roleId === userprofileRole.roleId &&
                    rolePermission.permissionId === permissionId
            );
            if (rolePermissions.length > 0) {
                return true;
            }
        }

        // Default: no permission
        return false;
    }

    //Public Methods

    /**
     * @param {number} regionId
     * @returns {boolean} Whether the current user has any location in the specified region.
     */
    static hasRegion(regionId) {
        const context = Security.context();

        if (!context || !regionId) {
            return false;
        }

        const {
            currentUser,
            userprofileLocationsIndexed,
            locationsIndexed
        } = context;

        const cacheKey = `hasRegion.${regionId}.${currentUser.id}`;

        if (!this.memo.hasOwnProperty(cacheKey)) {
            const userProfileLocations =
                Object.values(userprofileLocationsIndexed)
                    .filter((userprofileLocation) => {
                        return userprofileLocation.userprofileId === currentUser.id;
                    });

            const isUserInRegion =
                userProfileLocations.some((userprofileLocation) => {
                    const location = locationsIndexed[userprofileLocation.locationsId];

                    if (!location) {
                    return false;
                }

                    return location.regionId === regionId;
            });

            this.memo[cacheKey] = isUserInRegion;
        }

        return this.memo[cacheKey];
    }

    /**
     * @param {number} locationId
     * @returns {boolean} Whether the current user is assigned to the specified location.
     */
    static hasLocation(locationId) {
        const context = Security.context();

        if (!context || !locationId) {
            return false;
        }

        const {
            currentUser,
            userprofileLocationsIndexed
        } = context;

        const cacheKey = `hasLocation.${locationId}.${currentUser.id}`;

        if (!this.memo.hasOwnProperty(cacheKey)) {
            const isUserInLocation =
                Object.values(userprofileLocationsIndexed)
                    .some((userprofileLocation) => {
                        return (
                            userprofileLocation.userprofileId === currentUser.id &&
                            userprofileLocation.locationsId === locationId
                        );
                    });

            this.memo[cacheKey] = isUserInLocation;
        }

        return this.memo[cacheKey];
    }


}
